<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    @click="handleClick"
    v-bind="$attrs"
  >
    <span v-if="loading" class="btn-loading">
      <svg class="btn-spinner" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
          <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
          <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
        </circle>
      </svg>
    </span>
    <span v-if="icon && !loading" :class="['btn-icon', { 'btn-icon-only': !$slots.default }]">
      <component :is="icon" />
    </span>
    <span v-if="$slots.default && !loading" class="btn-text">
      <slot />
    </span>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'text', 'danger'].includes(value)
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [String, Object],
      default: null
    },
    fullWidth: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  computed: {
    buttonClasses() {
      return [
        'btn',
        `btn--${this.variant}`,
        `btn--${this.size}`,
        {
          'btn--disabled': this.disabled,
          'btn--loading': this.loading,
          'btn--full-width': this.fullWidth,
          'btn--icon-only': this.icon && !this.$slots.default
        }
      ]
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    }
  }
}
</script>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid transparent;
  border-radius: var(--border-radius-large);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
}

/* 尺寸变体 */
.btn--small {
  padding: 6px var(--spacing-sm);
  font-size: 12px;
  border-radius: var(--border-radius-medium);
}

.btn--medium {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: 14px;
}

.btn--large {
  padding: 12px var(--spacing-lg);
  font-size: 16px;
  border-radius: var(--border-radius-xl);
}

/* 颜色变体 */
.btn--primary {
  background-color: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.btn--primary:hover:not(.btn--disabled):not(.btn--loading) {
  background-color: var(--color-primary-hover);
  border-color: var(--color-primary-hover);
  box-shadow: var(--shadow-2);
}

.btn--secondary {
  background-color: var(--color-secondary);
  color: white;
  border-color: var(--color-secondary);
}

.btn--secondary:hover:not(.btn--disabled):not(.btn--loading) {
  background-color: #2d8f47;
  border-color: #2d8f47;
  box-shadow: var(--shadow-2);
}

.btn--outline {
  background-color: transparent;
  color: var(--color-primary);
  border-color: var(--color-outline);
}

.btn--outline:hover:not(.btn--disabled):not(.btn--loading) {
  background-color: var(--color-primary-light);
  border-color: var(--color-primary);
}

.btn--text {
  background-color: transparent;
  color: var(--color-primary);
  border-color: transparent;
}

.btn--text:hover:not(.btn--disabled):not(.btn--loading) {
  background-color: var(--color-primary-light);
}

.btn--danger {
  background-color: var(--color-error);
  color: white;
  border-color: var(--color-error);
}

.btn--danger:hover:not(.btn--disabled):not(.btn--loading) {
  background-color: #d33b2c;
  border-color: #d33b2c;
  box-shadow: var(--shadow-2);
}

/* 状态 */
.btn--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn--loading {
  cursor: not-allowed;
}

.btn--full-width {
  width: 100%;
}

.btn--icon-only {
  padding: var(--spacing-sm);
  aspect-ratio: 1;
}

.btn--icon-only.btn--small {
  padding: 6px;
}

.btn--icon-only.btn--large {
  padding: 12px;
}

/* 图标和加载状态 */
.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.btn-icon-only {
  margin: 0;
}

.btn-loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-text {
  display: flex;
  align-items: center;
}
</style>
